/**
 * RecurringJournalContext — Wave C.
 *
 * Templates for repeating journal entries (rent, depreciation, accruals).
 * Stores cadence + lines; `runDue()` posts every template whose nextRunDate
 * has arrived, then advances nextRunDate. Posting goes through
 * GeneralLedgerContext.postJournal with a per-run idempotency key so
 * accidentally calling `runDue()` twice on the same day is a no-op.
 *
 * Cadences supported: monthly (1st of month), quarterly (1st of Jan/Apr/Jul/Oct),
 * annually (Jan 1). User can also set an explicit nextRunDate / endDate.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { useGeneralLedger } from "../GeneralLedgerContext";
import type { JournalEntry } from "../glTypes";

export type RecurringCadence = "monthly" | "quarterly" | "annually";

export interface RecurringLine {
  accountId: string;
  amount: number;
  side: "debit" | "credit";
  memo?: string;
  jobId?: string;
  vendorId?: string;
  customerId?: string;
  tradeType?: string;
}

export interface RecurringTemplate {
  id: string;
  name: string;
  description: string;
  cadence: RecurringCadence;
  nextRunDate: string;           // yyyy-mm-dd
  endDate?: string;              // optional sunset date
  active: boolean;
  lines: RecurringLine[];
  createdAt: string;
  createdBy: string;
  lastRunAt?: string;
  lastRunJeId?: string;
  totalRuns: number;
}

interface NewTemplateInput {
  name: string;
  description: string;
  cadence: RecurringCadence;
  nextRunDate: string;
  endDate?: string;
  lines: RecurringLine[];
  createdBy?: string;
}

interface RecurringJournalContextValue {
  templates: RecurringTemplate[];
  createTemplate: (input: NewTemplateInput) => RecurringTemplate;
  updateTemplate: (id: string, patch: Partial<Omit<RecurringTemplate, "id" | "createdAt">>) => void;
  deactivate: (id: string) => void;
  /** Run every active template whose nextRunDate <= asOf. Returns posted JEs.
   *  Idempotency: re-running on the same day posts nothing extra. */
  runDue: (asOf?: string, by?: string) => { posted: JournalEntry[]; skipped: number };
  /** Force-run a single template right now (for testing). */
  runNow: (id: string, postingDate?: string, by?: string) => JournalEntry;
}

const RecurringJournalContext = createContext<RecurringJournalContextValue | null>(null);

const CENT = (n: number) => Math.round(n * 100);
const today = () => new Date().toISOString().slice(0, 10);

function advanceDate(iso: string, cadence: RecurringCadence): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (cadence === "monthly") {
    const dt = new Date(Date.UTC(y, m, d));   // m is 1-indexed → +1 month
    return dt.toISOString().slice(0, 10);
  }
  if (cadence === "quarterly") {
    const dt = new Date(Date.UTC(y, m + 2, d));
    return dt.toISOString().slice(0, 10);
  }
  // annually
  const dt = new Date(Date.UTC(y + 1, m - 1, d));
  return dt.toISOString().slice(0, 10);
}

export function RecurringJournalProvider({ children }: { children: React.ReactNode }) {
  const gl = useGeneralLedger();
  const [templates, setTemplates] = useState<RecurringTemplate[]>([]);

  const createTemplate = useCallback((input: NewTemplateInput): RecurringTemplate => {
    if (input.lines.length < 2) throw new Error("Template needs at least 2 lines.");
    let dr = 0, cr = 0;
    input.lines.forEach(l => {
      if (l.amount <= 0) throw new Error("All line amounts must be positive.");
      if (l.side === "debit") dr += l.amount; else cr += l.amount;
    });
    if (CENT(dr) !== CENT(cr)) {
      throw new Error(`Template out of balance: DR $${dr.toFixed(2)} ≠ CR $${cr.toFixed(2)}.`);
    }
    const tpl: RecurringTemplate = {
      id: `rec_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: input.name,
      description: input.description,
      cadence: input.cadence,
      nextRunDate: input.nextRunDate,
      endDate: input.endDate,
      active: true,
      lines: input.lines,
      createdAt: new Date().toISOString(),
      createdBy: input.createdBy ?? "Steve",
      totalRuns: 0,
    };
    setTemplates(prev => [tpl, ...prev]);
    return tpl;
  }, []);

  const updateTemplate = useCallback((id: string, patch: Partial<Omit<RecurringTemplate, "id" | "createdAt">>) => {
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
  }, []);

  const deactivate = useCallback((id: string) => {
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, active: false } : t));
  }, []);

  const postOne = useCallback((tpl: RecurringTemplate, postingDate: string, by: string): JournalEntry => {
    // Idempotency: include the run date in the key so the same template runs
    // exactly once per cadence anchor.
    const runKey = `${tpl.id}:${postingDate}`;
    return gl.postJournal({
      postingDate,
      description: `${tpl.name} — recurring (${tpl.cadence})`,
      source: "adjustment",
      sourceRef: { kind: "recurring_run", id: runKey },
      idempotencyKey: `recurring:${runKey}`,
      createdBy: by,
      lines: tpl.lines.map(l => ({ ...l })),
    });
  }, [gl]);

  const runDue = useCallback((asOf: string = today(), by = "Steve") => {
    const posted: JournalEntry[] = [];
    let skipped = 0;
    const updates: Array<{ id: string; nextRunDate: string; lastRunAt: string; lastRunJeId: string }> = [];

    for (const tpl of templates) {
      if (!tpl.active) continue;
      if (tpl.endDate && tpl.endDate < tpl.nextRunDate) continue;
      let runDate = tpl.nextRunDate;
      let lastJeId: string | undefined;
      let ran = 0;
      // Catch up: if we missed several cycles, post each.
      while (runDate <= asOf && (!tpl.endDate || runDate <= tpl.endDate)) {
        try {
          const je = postOne(tpl, runDate, by);
          posted.push(je);
          lastJeId = je.id;
          ran += 1;
        } catch {
          skipped += 1;
          break; // bail on first error (e.g. closed period); user can adjust nextRunDate.
        }
        runDate = advanceDate(runDate, tpl.cadence);
      }
      if (ran > 0 && lastJeId) {
        updates.push({
          id: tpl.id,
          nextRunDate: runDate,
          lastRunAt: new Date().toISOString(),
          lastRunJeId: lastJeId,
        });
      }
    }

    if (updates.length) {
      setTemplates(prev => prev.map(t => {
        const u = updates.find(x => x.id === t.id);
        return u
          ? { ...t, nextRunDate: u.nextRunDate, lastRunAt: u.lastRunAt, lastRunJeId: u.lastRunJeId,
              totalRuns: t.totalRuns + posted.filter(p => p.sourceRef?.id.startsWith(`${t.id}:`)).length }
          : t;
      }));
    }

    return { posted, skipped };
  }, [templates, postOne]);

  const runNow = useCallback((id: string, postingDate: string = today(), by = "Steve") => {
    const tpl = templates.find(t => t.id === id);
    if (!tpl) throw new Error("Template not found");
    if (!tpl.active) throw new Error("Template is inactive");
    const je = postOne(tpl, postingDate, by);
    setTemplates(prev => prev.map(t => t.id === id
      ? { ...t, lastRunAt: new Date().toISOString(), lastRunJeId: je.id, totalRuns: t.totalRuns + 1 }
      : t));
    return je;
  }, [templates, postOne]);

  // Auto-run on mount + once per day. runDue() is idempotent via per-run
  // sourceRef keys, so re-renders + HMR never double-post.
  const lastSweepRef = useRef<string>("");
  useEffect(() => {
    const t = today();
    if (lastSweepRef.current === t) return;
    lastSweepRef.current = t;
    try { runDue(t); } catch { /* silent — closed period etc. */ }
  }, [runDue]);

  const value = useMemo<RecurringJournalContextValue>(() => ({
    templates, createTemplate, updateTemplate, deactivate, runDue, runNow,
  }), [templates, createTemplate, updateTemplate, deactivate, runDue, runNow]);

  return <RecurringJournalContext.Provider value={value}>{children}</RecurringJournalContext.Provider>;
}

export function useRecurringJournals() {
  const ctx = useContext(RecurringJournalContext);
  if (!ctx) throw new Error("useRecurringJournals must be used inside <RecurringJournalProvider>");
  return ctx;
}
